import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { emptyTasks } from '../../constants/emptyChecklistTask';
import { getUserType } from '../../redux/slices/userSlice';
import { EmptyTasksType, Exam, TaskKeysType, WorkType } from '../../shared/types';
import { SummarizeTask } from '../../shared/types/task';
import ExcludesTask from '../ChecklistTasks/ExcludesTask';
import SelectOneTask from '../ChecklistTasks/SelectOneTask';
import TestTask from 'Components/ChecklistTasks/TestTask/TestTask';
import GapTask from 'Components/ChecklistTasks/GapTask/GapTask';
import TaskWithLink from '../ChecklistTasks/TaskWithLink';
import TextTask from '../ChecklistTasks/TextTask';

// removed task you can return them if need it

import MathFormulasTask from 'Components/ChecklistTasks/MathFormulasTask/MathFormulasTask';
import WordCompositionTask from 'Components/ChecklistTasks/WordCompositionTask/WordCompositionTask';
import WordStressTask from 'Components/ChecklistTasks/WordStressTask/WordStressTask';
import WordUnderlineTask from 'Components/ChecklistTasks/WordUnderlineTask/WordUnderlineTask';
import SentenceStructureTask from '../ChecklistTasks/SentenceStructureTask';

const taskTypeListAll = [
  //  you can use this array to return all tasks
  { name: 'textTask', value: 'text' },
  { name: 'taskWithLink', value: 'link' },
  { name: 'testTask', value: 'test' },
  { name: 'excludesTask', value: 'exclude' },
  { name: 'selectOneTask', value: 'selectOne' },
  { name: 'sentenceStructureTask', value: 'sentenceStructure' },
  { name: 'gapTask', value: 'gap' },
  { name: 'mathFormulasTask', value: 'mathFormulas' },
  { name: 'wordCompositionTask', value: 'wordComposition' },
  { name: 'wordStressTask', value: 'wordStress' },
  { name: 'wordUnderlineTask', value: 'underline' },
];

const taskTypeList = [
  { name: 'textTask', value: 'text' },
  { name: 'taskWithLink', value: 'link' },
  { name: 'testTask', value: 'test' },
  { name: 'excludesTask', value: 'exclude' },
  { name: 'selectOneTask', value: 'selectOne' },
  { name: 'gapTask', value: 'gap' },
];

const componentMapping = {
  text: TextTask,
  link: TaskWithLink,
  test: TestTask,
  exclude: ExcludesTask,
  selectOne: SelectOneTask,
  gap: GapTask,

  //sentenceStructure: SentenceStructureTask,
  //mathFormulas: MathFormulasTask,
  //wordComposition: WordCompositionTask,
  //wordStress: WordStressTask,
  //underline: WordUnderlineTask,
};

export default function ExamTasksForm({
  exam,
  tasks,
  setTasks,
  currentICLId,
  updateICL,
  workType,
}: {
  exam: Exam;
  tasks: SummarizeTask[];
  setTasks: (arr: SummarizeTask[]) => void;
  currentICLId?: string;
  updateICL: boolean;
  workType?: WorkType;
}) {
  const { t } = useTranslation();
  const userTypeSelector = useSelector(getUserType);

  const [newTaskType, setNewTaskType] = useState<string>('');

  const handleChangeNewTaskType = (taskType: keyof EmptyTasksType) => {
    setNewTaskType(taskType);
  };

  const handleAddNewTaskToChecklist = () => {
    addNewTaskToChecklist(newTaskType as keyof EmptyTasksType);
  };

  const addNewTaskToChecklist = (taskType: keyof EmptyTasksType) => {
    const newTaskArr: SummarizeTask[] = [...tasks];
    const id = uuidv4();
    const newTask = JSON.parse(JSON.stringify(emptyTasks[taskType]));
    const emptyTask: SummarizeTask = {
      ...newTask,
      id,
      indexNumber: newTaskArr.length + 1,
    };

    newTaskArr.push(emptyTask);
    setTasks(newTaskArr);
    setNewTaskType('');
  };

  const handleDeleteTask = (id: string) => {
    const updatedTaskArray: SummarizeTask[] = tasks.filter((task: SummarizeTask) => task.id !== id);

    setTasks(updatedTaskArray);
  };

  const handleChangeTaskByKey = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value };
    newTaskArray.splice(index, 1, updatedObject);

    setTasks(newTaskArray);
  };

  const renderEmptyTasks = () => {
    tasks.sort((a, b) => {
      if (a.indexNumber < b.indexNumber) {
        return -1;
      }

      return 0;
    });

    return tasks.map((it: SummarizeTask, index) => {
      const Component = componentMapping[it.type];

      return (
        <Component
          index={index}
          key={it.id}
          {...it}
          exam={exam}
          currentICLId={currentICLId}
          workType={workType}
          userType={userTypeSelector}
          handleDelete={handleDeleteTask}
          handleChangeTaskByKey={handleChangeTaskByKey}
          updateICL={updateICL}
        />
      );
    });
  };

  return (
    <>
      <div className="!mt-8 flex flex-col !gap-5 lg:!mt-12 lg:!gap-6">{renderEmptyTasks()}</div>

      <div className="!mt-5 flex flex-col items-center !gap-3 lg:!mt-6 lg:flex-row">
        <div className="w-full lg:w-1/3">
          <FormControl className="lg:w-1/4" size="small" fullWidth>
            <InputLabel>{t('task type')}</InputLabel>
            <Select
              sx={{ borderRadius: 25, width: '100%' }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderWidth: '2px',
                    borderColor: '#52C7FA',
                    marginTop: '6px',
                    borderRadius: '20px',
                  },
                },
              }}
              value={newTaskType}
              label={t('task type')}
              onChange={event => handleChangeNewTaskType(event.target.value as keyof EmptyTasksType)}
            >
              {taskTypeList.map(it => {
                return (
                  <MenuItem key={it.name} value={it.value}>
                    {t(it.name)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>

        <button
          className="btn btn-primary more flex w-full items-center gap-[10px] lg:w-2/6"
          onClick={() => {
            handleAddNewTaskToChecklist();
          }}
          disabled={!newTaskType}
        >
          <span className="icon icon-plus"></span>
          <p className="!mb-0 text-lg font-bold">{t('add task')}</p>
        </button>
      </div>
    </>
  );
}
